import React, { useEffect, useMemo, useState } from "react";
import { GraphMenu } from "components/DataExplorationV2/GraphMenu";
import { GraphMenuSection } from "components/DataExplorationV2/GraphMenuSection";
import { ConfigureSeriesSection } from "components/DataExplorationV2/ConfigureMenu/SeriesSection/ConfigureSeriesSection";
import { XAxisSection } from "components/DataExplorationV2/ConfigureMenu/XAxisSection/XAxisSection";
import { YAxisSection } from "components/DataExplorationV2/ConfigureMenu/YAxisSection/YAxisSection";
import { ReferenceLineSection } from "components/DataExplorationV2/ConfigureMenu/ReferenceLineSection/ReferenceLineSection";

/** Typescript Interfaces */
import { Tag } from "interfaces/tag";
import { Machine } from "interfaces/machine";
import {
  ScadaDatetimeTagDataSource,
  LineSeries,
  YAxis,
  XAxis,
  Axis,
  ReferenceLine
} from "interfaces/chart";
import { Component } from "interfaces/component";
import { useGetSeriesRange } from "hooks/data-exploration-v2/chart-menu-hooks";

enum Section {
  SERIES = "series",
  X_AXIS = "x-axis",
  Y_AXIS = "y-axis",
  REFERENCE_LINE = "reference-line"
}

export interface ConfigureProps {
  /** The status of API fetch for Parameters(Tags) and Machines */
  loading: boolean;
  seriesError: boolean;
  /** Component data */
  components: Component[] | null;
  /** Parameter(Tag) data and handlers */
  parameters: Tag[] | null;
  selectedParameter: Tag | null;
  onChangeParameter: (newParameter: Tag | null) => void;
  /** Machine data and handlers */
  machines: Machine[] | null;
  selectedMachine: Machine | null;
  onChangeMachine: (newMachine: Machine | null) => void;
  /** Site configure switches */
  siteAverageEnabled: boolean;
  siteRangeEnabled: boolean;
  onChangeSiteAverageEnabled: (newValue: boolean) => void;
  onChangeSiteRangeEnabled: (newValue: boolean) => void;
  /** Series data for handler */
  series: LineSeries<ScadaDatetimeTagDataSource>[];
  selectedSeries: LineSeries<ScadaDatetimeTagDataSource> | null;
  onChangeSelectedSeries: (
    series: LineSeries<ScadaDatetimeTagDataSource>
  ) => void;
  onAddSeries: () => void;
  onDeleteSeries: (seriesId: string) => void;
  /** X Axis configure  */
  startDate: Date | null;
  endDate: Date | null;
  xAxisLabelEnabled: boolean;
  onChangeStartDate: (startDate: Date | null) => void;
  onChangeEndDate: (endDate: Date | null) => void;
  onChangeXAxisLabelEnabled: (newValue: boolean) => void;
  /** Y Axis configure  */
  yAxes: YAxis[];
  selectedYAxis: YAxis | null;
  onChangeSelectedYAxis: (yAxis: YAxis) => void;
  onChangeYAxisMin: (min: number | null) => void;
  onChangeYAxisMax: (max: number | null) => void;
  yAxisLabelEnabled: boolean;
  onChangeYAxisLabelEnabled: (newValue: boolean) => void;
  /** Reference line */
  xAxes: XAxis[];
  referenceLines: ReferenceLine[];
  selectedReferenceLine: ReferenceLine | null;
  onChangeSelectedReferenceLine: (referenceLine: ReferenceLine | null) => void;
  onChangeReferenceLineAxis: (axis: Axis | null) => void;
  onChangeReferenceLineValue: (value: number | string | Date | null) => void;
  onAddReferenceLine: () => void;
  onDeleteReferenceLine: (referenceLineId: string) => void;
  onChangeReferenceLineTitle: (
    referenceLine: ReferenceLine,
    title: string | null
  ) => void;
}

export const ConfigureMenu = React.memo(function ConfigureMenu({
  loading,
  seriesError,
  components,
  parameters,
  machines,
  selectedParameter,
  selectedMachine,
  onChangeParameter,
  onChangeMachine,
  siteAverageEnabled,
  siteRangeEnabled,
  onChangeSiteAverageEnabled,
  onChangeSiteRangeEnabled,
  series,
  selectedSeries,
  onChangeSelectedSeries,
  onAddSeries,
  onDeleteSeries,
  startDate,
  endDate,
  xAxes,
  xAxisLabelEnabled,
  onChangeStartDate,
  onChangeEndDate,
  onChangeXAxisLabelEnabled,
  yAxes,
  selectedYAxis,
  onChangeSelectedYAxis,
  onChangeYAxisMin,
  onChangeYAxisMax,
  yAxisLabelEnabled,
  onChangeYAxisLabelEnabled,
  referenceLines,
  selectedReferenceLine,
  onChangeSelectedReferenceLine,
  onChangeReferenceLineAxis,
  onChangeReferenceLineValue,
  onAddReferenceLine,
  onDeleteReferenceLine,
  onChangeReferenceLineTitle
}: ConfigureProps): JSX.Element {
  const [expandedSection, setExpandedSection] = useState<string | null>(
    "series"
  );
  const { range, error: rangeError } = useGetSeriesRange(series);

  const handleToggleSection = (sectionKey: string): void => {
    setExpandedSection(prevState =>
      prevState !== sectionKey ? sectionKey : null
    );
  };

  const { minDate, maxDate } = useMemo(() => {
    if (range) {
      return {
        minDate: new Date(range.min_date),
        maxDate: new Date(range.max_date)
      };
    }

    return { minDate: null, maxDate: null };
  }, [range]);

  // The dates to show in the slider and date picker components.
  // These are always up-to-date but are not immediately reflected in the chart state.
  const [internalStartDate, setInternalStartDate] = useState(
    startDate ?? minDate
  );
  const [internalEndDate, setInternalEndDate] = useState(endDate ?? maxDate);

  // Debounce the changes to the chart state by waiting for the user to stop making changes for a short period of time
  useEffect(() => {
    const timer = setTimeout(() => {
      onChangeStartDate(internalStartDate);
      onChangeEndDate(internalEndDate);
    }, 500);
    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalStartDate, internalEndDate]);

  // Reset the internal date values when all series have been deleted
  useEffect(() => {
    if (series.length === 0) {
      setInternalStartDate(null);
      setInternalEndDate(null);
    }
  }, [series.length]);

  return (
    <GraphMenu title="Configure">
      <GraphMenuSection
        title="Series"
        expanded={expandedSection === Section.SERIES}
        onToggleExpanded={() => handleToggleSection(Section.SERIES)}
        compact={true}
      >
        <ConfigureSeriesSection
          loading={loading}
          error={seriesError}
          components={components}
          parameters={parameters}
          machines={machines}
          selectedParameter={selectedParameter}
          selectedMachine={selectedMachine}
          onChangeParameter={onChangeParameter}
          onChangeMachine={onChangeMachine}
          siteAverageEnabled={siteAverageEnabled}
          siteRangeEnabled={siteRangeEnabled}
          onChangeSiteAverageEnabled={onChangeSiteAverageEnabled}
          onChangeSiteRangeEnabled={onChangeSiteRangeEnabled}
          series={series}
          selectedSeries={selectedSeries}
          onChangeSelectedSeries={onChangeSelectedSeries}
          onAddSeries={onAddSeries}
          onDeleteSeries={onDeleteSeries}
        />
      </GraphMenuSection>
      <XAxisSection
        expanded={expandedSection === Section.X_AXIS}
        onToggleExpanded={() => handleToggleSection(Section.X_AXIS)}
        loading={loading}
        error={seriesError || rangeError}
        minDate={minDate}
        maxDate={maxDate}
        startDate={internalStartDate}
        endDate={internalEndDate}
        xAxisLabelEnabled={xAxisLabelEnabled}
        onChangeStartDate={setInternalStartDate}
        onChangeEndDate={setInternalEndDate}
        onChangeXAxisLabelEnabled={onChangeXAxisLabelEnabled}
      />
      <GraphMenuSection
        title="Y Axis"
        expanded={expandedSection === Section.Y_AXIS}
        onToggleExpanded={() => handleToggleSection(Section.Y_AXIS)}
        compact={true}
      >
        <YAxisSection
          loading={loading}
          error={seriesError}
          yAxes={yAxes}
          selectedYAxis={selectedYAxis}
          onChangeSelectedYAxis={onChangeSelectedYAxis}
          onChangeYAxisMin={onChangeYAxisMin}
          onChangeYAxisMax={onChangeYAxisMax}
          yAxisLabelEnabled={yAxisLabelEnabled}
          onChangeYAxisLabelEnabled={onChangeYAxisLabelEnabled}
        />
      </GraphMenuSection>

      <GraphMenuSection
        title="Reference line"
        expanded={expandedSection === Section.REFERENCE_LINE}
        onToggleExpanded={() => handleToggleSection(Section.REFERENCE_LINE)}
        compact={true}
      >
        <ReferenceLineSection
          loading={loading}
          error={rangeError}
          yAxes={yAxes}
          xAxes={xAxes}
          minDate={minDate}
          maxDate={maxDate}
          referenceLines={referenceLines}
          selectedReferenceLine={selectedReferenceLine}
          onChangeSelectedReferenceLine={onChangeSelectedReferenceLine}
          onChangeReferenceLineAxis={onChangeReferenceLineAxis}
          onChangeReferenceLineValue={onChangeReferenceLineValue}
          onAddReferenceLine={onAddReferenceLine}
          onDeleteReferenceLine={onDeleteReferenceLine}
          onChangeReferenceLineTitle={onChangeReferenceLineTitle}
        />
      </GraphMenuSection>
    </GraphMenu>
  );
});
